/**
 * Area Context - Provides area-aware navigation and labels to feature components.
 *
 * Each feature area (packets, checklists, contacts) wraps its routes with an AreaProvider.
 * Child components use useArea() to get navigation helpers without knowing their area key.
 *
 * @example
 * // In routes/index.tsx - wrap area routes
 * <AreaProvider area="checklists">
 *   <Outlet />
 * </AreaProvider>
 *
 * // In any child component - no area key needed
 * const { goBack, labels } = useArea();
 * <Button onClick={goBack}>{labels.back}</Button>
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { areas, navigationLabels, type AreaKey, type Area } from '@/constants';

interface AreaContextValue {
  key: AreaKey;
  area: Area;
}

const AreaContext = createContext<AreaContextValue | null>(null);

interface AreaProviderProps {
  area: AreaKey;
  children: ReactNode;
}

export function AreaProvider({ area, children }: AreaProviderProps) {
  const value = useMemo(() => ({
    key: area,
    area: areas[area],
  }), [area]);

  return <AreaContext.Provider value={value}>{children}</AreaContext.Provider>;
}

/**
 * Hook to access current area context with navigation helpers.
 *
 * Must be used within an AreaProvider (i.e., within a feature area's routes).
 *
 * @returns Area information and navigation helpers
 * @throws Error if used outside of an AreaProvider
 *
 * @example
 * const { goBack, labels, area } = useArea();
 *
 * // Navigate back to area list
 * <Button onClick={goBack}>{labels.back}</Button>
 *
 * // Access area info
 * <Text>Editing {area.nameSingular}</Text>
 */
export function useArea() {
  const context = useContext(AreaContext);
  const navigate = useNavigate();

  if (!context) {
    throw new Error('useArea must be used within an AreaProvider. Wrap your routes with <AreaProvider area="...">.');
  }

  const { key, area } = context;

  return useMemo(() => ({
    /** The area key (e.g., 'checklists') */
    key,

    /** The area definition (name, nameSingular, path) */
    area,

    /** Navigate back to the area list */
    goBack: () => navigate(area.path),

    /** Pre-generated labels for this area */
    labels: {
      /** "Back to Checklists" */
      back: navigationLabels.backTo(key),
      /** "Create Checklist" */
      create: navigationLabels.create(key),
      /** "Edit Checklist" */
      edit: navigationLabels.edit(key),
      /** "View Checklist" */
      view: navigationLabels.view(key),
      /** "No checklists found" */
      noItemsFound: navigationLabels.noItemsFound(key),
      /** "No checklists yet" */
      noItemsYet: navigationLabels.noItemsYet(key),
    },
  }), [key, area, navigate]);
}
